import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ subscription: null, is_pro: false, effective_status: "free", plan: null, eligible_plan: null });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user || user === false || user.role === "admin") {
      setState({ subscription: null, is_pro: user?.role === "admin", effective_status: "free", plan: null, eligible_plan: null });
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/subscription/me");
      setState(data);
    } catch {
      setState({ subscription: null, is_pro: false, effective_status: "free", plan: null, eligible_plan: null });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const startTrial = async (planId) => {
    const { data } = await api.post("/subscription/start-trial", { plan_id: planId });
    await refresh();
    return data;
  };

  const createCheckout = async (planId) => {
    const origin_url = window.location.origin;
    const { data } = await api.post("/subscription/checkout", { plan_id: planId, origin_url });
    return data; // { url, session_id }
  };

  const cancel = async () => {
    const { data } = await api.post("/subscription/cancel", {});
    await refresh();
    return data;
  };

  const pollCheckout = async (sessionId) => {
    const { data } = await api.get(`/subscription/checkout-status/${sessionId}`);
    return data;
  };

  return (
    <SubscriptionContext.Provider value={{ ...state, loading, refresh, startTrial, createCheckout, cancel, pollCheckout }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be used within SubscriptionProvider");
  return ctx;
}

export function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}
